/**
 * Plain constants and pure helpers with NO zod dependency. Browser code imports these; keeping them
 * out of the schema modules lets the bundler leave zod out of the web bundle.
 */

// ---------- realtime ----------

export const PROTOCOL_VERSION = 1;
/** Hard ceiling enforced in code; remote config may only lower the effective limit. */
export const MESSAGE_BODY_HARD_LIMIT = 16_384;
/** The sender may edit a text message for this long after sending. */
export const MESSAGE_EDIT_WINDOW_MINUTES = 15;
/** The sender may delete a message for everyone for this long after sending. */
export const MESSAGE_DELETE_WINDOW_HOURS = 48;
/** Hard ceiling for one upload; remote config may only lower it. */
export const MAX_UPLOAD_BYTES = 50 * 1024 * 1024;
/** Longest accepted voice message. */
export const MAX_VOICE_MS = 10 * 60 * 1000;
/** Largest client→server WebSocket frame the gateway accepts (bytes). */
export const MAX_FRAME_BYTES = 64 * 1024;

// ---------- push ----------

/**
 * Hosts of the browser vendors' push services. Subscriptions pointing anywhere else are refused:
 * otherwise the server could be made to send authenticated requests to arbitrary URLs (SSRF).
 */
export const PUSH_SERVICE_HOST_SUFFIXES = [
  'fcm.googleapis.com',
  'android.googleapis.com',
  'updates.push.services.mozilla.com',
  'push.services.mozilla.com',
  'notify.windows.com',
  'push.apple.com',
] as const;

export function isAllowedPushEndpoint(raw: string): boolean {
  let url: URL;
  try {
    url = new URL(raw);
  } catch {
    return false;
  }
  if (url.protocol !== 'https:' || url.port !== '' || url.username || url.password) return false;
  const host = url.hostname.toLowerCase();
  return PUSH_SERVICE_HOST_SUFFIXES.some((s) => host === s || host.endsWith(`.${s}`));
}

export const NOTIFICATION_PREVIEWS = ['full', 'sender', 'hidden'] as const;
export type NotificationPreview = (typeof NOTIFICATION_PREVIEWS)[number];

/** Mute presets offered in the UI; `null` unmutes, `forever` mutes until undone. */
export const MUTE_PRESETS = ['1h', '8h', '1w', 'forever'] as const;

// ---------- reports ----------

export const REPORT_REASONS = [
  'spam',
  'harassment',
  'hate',
  'sexual_content',
  'child_safety',
  'self_harm',
  'violence',
  'impersonation',
  'scam',
  'other',
] as const;
export type ReportReason = (typeof REPORT_REASONS)[number];

/** Maximum messages a reporter can attach as evidence. */
export const MAX_REPORT_EVIDENCE = 20;

/** 1 = most urgent. Anything that may involve harm to a person, or a minor, goes to the front. */
export function reportPriority(reason: ReportReason, minorInvolved: boolean): 1 | 2 | 3 | 4 {
  if (reason === 'child_safety' || reason === 'self_harm') return 1;
  const base = reason === 'violence' || reason === 'sexual_content' || reason === 'hate' ? 2 : reason === 'other' ? 4 : 3;
  return minorInvolved ? (Math.max(1, base - 1) as 1 | 2 | 3) : base;
}
